/**
 * Keeps keyboard focus inside an open dialog.
 *
 * Dialogs are teleported to <body>, so without this the Tab key walks through
 * the whole page behind the backdrop. While the dialog is open every other
 * top-level element in <body> is marked `inert`, which makes the page behind
 * unfocusable and unreachable for keyboard/assistive tech. Only the dialog's
 * own controls remain in the Tab order. A Tab-wrapping fallback keeps focus
 * inside even in browsers without `inert` support.
 */
const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

const isVisible = (el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);

export function activateFocusTrap(root) {
  if (!root) return () => {};
  const opener = document.activeElement;
  const focusables = () => Array.from(root.querySelectorAll(FOCUSABLE)).filter(isVisible);

  // 1) Make everything outside the dialog inert (remember only what we changed,
  //    so nested dialogs restore correctly).
  const madeInert = [];
  Array.from(document.body.children).forEach((el) => {
    if (el === root || el.contains(root) || /^(SCRIPT|STYLE|LINK)$/.test(el.tagName)) return;
    if (!el.hasAttribute('inert')) {
      el.setAttribute('inert', '');
      madeInert.push(el);
    }
  });

  // 2) Fallback wrap for Tab / Shift+Tab at the dialog edges.
  const onKeydown = (event) => {
    if (event.key !== 'Tab') return;
    const items = focusables();
    if (!items.length) {
      event.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    const outside = !root.contains(active);

    if (event.shiftKey && (active === first || outside)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || outside)) {
      event.preventDefault();
      first.focus();
    }
  };
  document.addEventListener('keydown', onKeydown, true);

  // 3) Move focus onto the dialog container itself (not into a field), so the
  //    first Tab press lands on the first focusable element in natural DOM order.
  if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1');
  requestAnimationFrame(() => root.focus({preventScroll: true}));

  return function release() {
    document.removeEventListener('keydown', onKeydown, true);
    madeInert.forEach(el => el.removeAttribute('inert'));
    if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
      opener.focus({preventScroll: true});
    }
  };
}
